import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { axios } from '../hooks/useAuth';
import SongCard from '../components/SongCard';
import '../styles/songs.css';

const GENRES = ['All', 'Pop', 'Rock', 'R&B', 'Hip Hop', 'Country', 'Jazz', 'Electronic', 'Latin', 'Classical'];
const DIFFICULTIES = ['All', 'easy', 'medium', 'hard'];
const SORT_OPTIONS = [
  { value: 'title', label: 'Title A-Z' },
  { value: 'artist', label: 'Artist A-Z' },
  { value: 'viewCount', label: 'Most Viewed' },
  { value: 'singCount', label: 'Most Sung' },
  { value: 'createdAt', label: 'Newest' },
];

export default function SongListPage() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('All');
  const [difficulty, setDifficulty] = useState('All');
  const [sortBy, setSortBy] = useState('title');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchSongs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        sortBy,
        ...(genre !== 'All' && { genre }),
        ...(difficulty !== 'All' && { difficulty }),
      });

      let res;
      if (search.trim()) {
        res = await axios.get(`/api/songs/search?q=${encodeURIComponent(search)}&${params}`);
        setSongs(res.data.songs);
        setTotal(res.data.songs.length);
        setTotalPages(1);
      } else {
        res = await axios.get(`/api/songs?${params}`);
        setSongs(res.data.songs);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch (err) {
      console.error('Failed to fetch songs:', err);
    } finally {
      setLoading(false);
    }
  }, [search, genre, difficulty, sortBy, page]);

  useEffect(() => {
    const timer = setTimeout(fetchSongs, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchSongs]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [search, genre, difficulty, sortBy]);

  return (
    <div className="songs-page page-enter">
      {/* Header */}
      <div className="songs-header">
        <div className="songs-header-text">
          <h1 className="gradient-text">🎵 Song Library</h1>
          <p className="text-secondary">{total} songs available to sing</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="songs-filters card">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search songs, artists..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="search-clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <span className="filter-label">Genre</span>
            <div className="filter-chips">
              {GENRES.map(g => (
                <button
                  key={g}
                  className={`filter-chip ${genre === g ? 'active' : ''}`}
                  onClick={() => setGenre(g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <span className="filter-label">Difficulty</span>
            <div className="filter-chips">
              {DIFFICULTIES.map(d => (
                <button
                  key={d}
                  className={`filter-chip ${difficulty === d ? 'active' : ''}`}
                  onClick={() => setDifficulty(d)}
                >
                  {d === 'All' ? 'All' : `${d.charAt(0).toUpperCase() + d.slice(1)}`}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group" style={{ marginLeft: 'auto' }}>
            <span className="filter-label">Sort by</span>
            <select
              className="form-input"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{ minWidth: 140 }}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Songs Grid */}
      {loading ? (
        <div className="songs-loading">
          {[...Array(8)].map((_, i) => (
            <div key={i} className={`song-skeleton delay-${(i % 4) * 100}`} />
          ))}
        </div>
      ) : songs.length === 0 ? (
        <div className="songs-empty">
          <div style={{ fontSize: 64 }}>🎵</div>
          <h3>No songs found</h3>
          <p className="text-muted">
            {search ? `No results for "${search}"` : 'No songs match the selected filters'}
          </p>
          <button className="btn btn-secondary" onClick={() => { setSearch(''); setGenre('All'); setDifficulty('All'); }}>
            Clear Filters
          </button>
        </div>
      ) : (
        <>
          <div className="songs-grid">
            {songs.map((song, i) => (
              <SongCard key={song.id} song={song} index={i} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
